import { Box, Stack, Typography, useTheme } from "@mui/material";
import { DateTime } from "luxon";

import Robot from "resources/images/robot.png";
import PrivateBo from "resources/images/fedora-bo.png";
import ChatMarkdown from "app/containers/Chatbot/ChatMarkdown";
import { ToolCallCompleted } from "app/containers/Chatbot/ToolCallAccordion";

type MessageProps = {
  role: "BO" | "USER";
  sentTimeStamp: DateTime;
  content: string;
  toolCalls?: {
    label: string;
    success: boolean;
  }[];
};

export const Message = (props: Readonly<MessageProps>) => {
  return (
    <Box display="block">
      {props.role === "BO" ? (
        <BoMessage {...props} />
      ) : (
        <UserMessage {...props} />
      )}
    </Box>
  );
};

export const BoAvatar = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <div className="shrink-0">
      <div
        className={`rounded-full ${isDark ? "bg-sapphire-100" : "bg-gold-100"}`}
      >
        <img
          src={isDark ? PrivateBo : Robot}
          alt="coach-bo"
          className="w-[30px] aspect-square"
        />
      </div>
    </div>
  );
};

const BoMessage = ({
  sentTimeStamp,
  content,
  toolCalls,
}: Readonly<MessageProps>) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Stack direction="row" columnGap=".5rem" maxWidth="75%">
      <BoAvatar />
      <Stack paddingTop=".5rem" direction="column" rowGap=".5rem">
        <Box display="flex" flexDirection="row">
          <Typography
            variant="chatSender"
            className={
              isDark ? "text-teamApp-text-disabled-dark" : "text-navy-100"
            }
          >
            Coach Bo
          </Typography>

          <Typography
            variant="chatTimeStamp"
            className={
              isDark
                ? "text-teamApp-text-muted-dark"
                : "text-teamApp-text-secondary"
            }
            sx={{
              paddingLeft: ".25rem",
            }}
          >
            {sentTimeStamp.toFormat("t")}
          </Typography>
        </Box>
        {toolCalls && toolCalls.length > 0 && (
          <div className="flex flex-col gap-0.5">
            {toolCalls.map((tc, i) => (
              <ToolCallCompleted
                key={i} // NOSONAR: append-only list with no stable id; labels can repeat
                label={tc.label}
                success={tc.success}
              />
            ))}
          </div>
        )}
        <Box
          className={`chat-markdown ${
            isDark ? "bg-teamApp-dark-surface" : "bg-white"
          }`}
          borderRadius=".25rem .75rem .75rem .75rem"
          padding="1rem"
          boxShadow={
            isDark
              ? "0px 4px 8px 0px var(--team-app-shadow-dark-heavy)"
              : "0px 4px 8px 0px #0020511a"
          }
        >
          <Typography variant="chatMessageBody" component="div">
            <ChatMarkdown>{content}</ChatMarkdown>
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};

const UserMessage = (props: Readonly<MessageProps>) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Stack
      direction="row"
      width="fit-content"
      marginLeft="auto"
      columnGap=".5rem"
      maxWidth="75%"
      className="bg-sapphire-100"
      borderRadius=".75rem .75rem .25rem .75rem"
      padding="1rem"
      boxShadow={
        isDark
          ? "0px 4px 8px 0px var(--team-app-shadow-dark-heavy)"
          : "0px 4px 8px 0px #3a5dce1a"
      }
    >
      <Box className="chat-markdown">
        <Typography
          variant="chatMessageBody"
          component="div"
          className="text-white"
        >
          <ChatMarkdown>{props.content}</ChatMarkdown>
        </Typography>
      </Box>
    </Stack>
  );
};
